import { recruitingAPI } from "../../services/recruitingApi";
import {
  createPersonnelAssignmentHook,
  type UsePersonnelAssignmentOptions,
  type AssignedPerson,
  type AvailablePerson,
  type NotificationState,
  type ConfirmDialogState,
} from "./usePersonnelAssignment";

export interface AssignedKam extends AssignedPerson {
  kam_user_id: number;
  kam_name?: string;
}

export type AvailableKam = AvailablePerson;

const usePersonnelAssignment = createPersonnelAssignmentHook<AssignedKam>({
  roleSlug: "sales",
  assignedQueryKey: "application-kams",
  availableQueryKey: "sales-list",
  assignedDataKey: "assigned_kams",
  fetchAssigned: (applicationId) =>
    recruitingAPI.getKamsForApplication(applicationId),
  assign: (applicationId, kamUserId) =>
    recruitingAPI.assignKamToApplication(applicationId, kamUserId),
  remove: (applicationId, kamUserId) =>
    recruitingAPI.removeKamFromApplication(applicationId, kamUserId),
  label: "KAM",
  gateAssignedOnPermission: false,
  getPersonnelId: (k) => k.kam_user_id,
});

export interface UseKamAssignmentReturn {
  kams: AssignedKam[];
  availableKams: AvailableKam[];
  selectedKamId: string;
  setSelectedKamId: (id: string) => void;
  isAssigningKam: boolean;
  handleAssignKam: (overrideId?: string) => Promise<void>;
  handleRemoveKam: (kamUserId: number) => void;
  canManageKams: boolean;
  notification: NotificationState;
  closeNotification: () => void;
  confirmDialog: ConfirmDialogState;
  confirmRemoval: () => void;
  cancelRemoval: () => void;
}

/**
 * Custom hook for managing KAM (sales) assignments for an application.
 * Wraps the shared usePersonnelAssignment hook with KAM-specific naming.
 */
export function useKamAssignment(
  options: UsePersonnelAssignmentOptions,
): UseKamAssignmentReturn {
  const result = usePersonnelAssignment(options);

  return {
    kams: result.assigned,
    availableKams: result.available,
    selectedKamId: result.selectedId,
    setSelectedKamId: result.setSelectedId,
    isAssigningKam: result.isAssigning,
    handleAssignKam: result.handleAssign,
    handleRemoveKam: result.handleRemove,
    canManageKams: result.canManage,
    notification: result.notification,
    closeNotification: result.closeNotification,
    confirmDialog: result.confirmDialog,
    confirmRemoval: result.confirmRemoval,
    cancelRemoval: result.cancelRemoval,
  };
}

export default useKamAssignment;
